import React, { useEffect, useState } from 'react';
import { Button, Spinner, Table, Form } from 'react-bootstrap';
import {
    collection,
    doc,
    onSnapshot,
    query,
    setDoc,
    updateDoc,
    where,
} from 'firebase/firestore';
import { db } from '../Firebase/firebase';

const STATUS_STYLE = {
    pending: { color: '#a07000', label: 'Parsing…' },
    parsed: { color: '#1a7f37', label: 'Parsed' },
    failed: { color: '#b42318', label: 'Failed' },
};

function formatTimestamp(ts) {
    if (!ts) return '';
    const d = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
    return d.toLocaleString();
}

async function setActive(team, heatsheetId) {
    await setDoc(
        doc(db, 'meetstatus', team),
        { currentmeet: heatsheetId, currentstart: 0 },
        { merge: true },
    );
}

async function retry(user, heatsheetId) {
    await updateDoc(doc(db, 'heatsheets', heatsheetId), {
        status: 'pending',
        errorMessage: null,
    });
    const token = await user.getIdToken();
    const res = await fetch(`/api/heatsheets/${heatsheetId}/retry`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) {
        throw new Error(`Retry failed: HTTP ${res.status}`);
    }
}

async function preview(user, heatsheetId) {
    const token = await user.getIdToken();
    const res = await fetch(`/api/heatsheets/${heatsheetId}/preview-url`, {
        headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) {
        throw new Error(`Preview failed: HTTP ${res.status}`);
    }
    const { url } = await res.json();
    window.open(url, '_blank', 'noopener,noreferrer');
}

function downloadJson(row) {
    // The parsed meet data lives on the row already (Firestore snapshot), so we
    // can export it without a backend round-trip. Emit the same {opponent, starts}
    // shape the uploader accepts, so a downloaded file round-trips back in.
    const meet = { opponent: row.opponent ?? null, starts: row.starts ?? [] };
    const blob = new Blob([JSON.stringify(meet, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const base = (row.displayName || row.id).replace(/\.(pdf|json)$/i, '');
    const a = document.createElement('a');
    a.href = url;
    a.download = `${base}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

async function destroy(user, heatsheetId) {
    const token = await user.getIdToken();
    const res = await fetch(`/api/heatsheets/${heatsheetId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok && res.status !== 204) {
        throw new Error(`Delete failed: HTTP ${res.status}`);
    }
}

function NoteEditor({ row }) {
    const [value, setValue] = useState(row.note || '');
    const [saved, setSaved] = useState(true);

    useEffect(() => {
        setValue(row.note || '');
    }, [row.note]);

    const save = async () => {
        if (value === (row.note || '')) return;
        try {
            await updateDoc(doc(db, 'heatsheets', row.id), { note: value });
            setSaved(true);
        } catch (e) {
            setSaved(false);
        }
    };

    return (
        <Form.Control
            as="textarea"
            rows={1}
            size="sm"
            placeholder="Add a note…"
            value={value}
            onChange={(e) => { setValue(e.target.value); setSaved(false); }}
            onBlur={save}
            onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    e.target.blur();
                }
            }}
            style={{ minWidth: 160, background: saved ? undefined : '#fff7d6' }}
        />
    );
}

export default function HeatsheetList({ team, user }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeId, setActiveId] = useState(null);
    const [actionError, setActionError] = useState(null);
    const [confirmingDeleteId, setConfirmingDeleteId] = useState(null);

    useEffect(() => {
        if (!team) return;
        setLoading(true);
        // Sort client-side to avoid needing a Firestore composite index — per-team
        // heat sheet counts are small (dozens at most).
        const q = query(
            collection(db, 'heatsheets'),
            where('accountID', '==', team),
        );
        const unsub = onSnapshot(q, (snap) => {
            const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            rows.sort((a, b) => {
                const ta = a.uploadedAt?.toMillis?.() ?? 0;
                const tb = b.uploadedAt?.toMillis?.() ?? 0;
                return tb - ta;
            });
            setRows(rows);
            setLoading(false);
        }, (err) => {
            setActionError(`Couldn't load heat sheets: ${err.message}`);
            setLoading(false);
        });
        return unsub;
    }, [team]);

    useEffect(() => {
        if (!team) return;
        const unsub = onSnapshot(doc(db, 'meetstatus', team), (snap) => {
            setActiveId(snap.exists() ? snap.data().currentmeet : null);
        });
        return unsub;
    }, [team]);

    if (loading) {
        return (
            <p style={{ color: '#666' }}>
                <Spinner size="sm" animation="border" /> Loading heat sheets…
            </p>
        );
    }

    if (!rows.length) {
        return <p>No heat sheets uploaded yet.</p>;
    }

    return (
        <>
            {actionError && <p style={{ color: 'crimson' }}>{actionError}</p>}
            <Table striped hover responsive>
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Uploaded</th>
                        <th>Status</th>
                        <th>Note</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => {
                        const style = STATUS_STYLE[row.status] || { color: 'gray', label: row.status || 'unknown' };
                        const isActive = row.id === activeId;
                        const confirming = confirmingDeleteId === row.id;
                        return (
                            <tr key={row.id}>
                                <td>
                                    {row.displayName || row.id}
                                    {isActive && <strong style={{ marginLeft: 8 }}>(active)</strong>}
                                </td>
                                <td>{formatTimestamp(row.uploadedAt)}</td>
                                <td style={{ color: style.color }}>
                                    {row.status === 'pending' && <Spinner size="sm" animation="border" />}{' '}
                                    {style.label}
                                    {row.status === 'failed' && row.errorMessage && (
                                        <div style={{ fontSize: '0.8em', color: '#666' }}>{row.errorMessage}</div>
                                    )}
                                </td>
                                <td><NoteEditor row={row} /></td>
                                <td>
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                        <Button
                                            size="sm"
                                            variant="outline-primary"
                                            onClick={() => preview(user, row.id).catch((e) => setActionError(e.message))}
                                        >
                                            Preview
                                        </Button>
                                        {row.status === 'parsed' && !isActive && (
                                            <Button
                                                size="sm"
                                                onClick={() => setActive(team, row.id).catch((e) => setActionError(e.message))}
                                            >
                                                Set as active
                                            </Button>
                                        )}
                                        {row.status === 'parsed' && (
                                            <Button
                                                size="sm"
                                                variant="outline-secondary"
                                                onClick={() => downloadJson(row)}
                                            >
                                                Download JSON
                                            </Button>
                                        )}
                                        {row.status === 'failed' && (
                                            <Button
                                                size="sm"
                                                variant="outline-secondary"
                                                onClick={() => retry(user, row.id).catch((e) => setActionError(e.message))}
                                            >
                                                Retry
                                            </Button>
                                        )}
                                        {!confirming && (
                                            <Button
                                                size="sm"
                                                variant="outline-danger"
                                                onClick={() => setConfirmingDeleteId(row.id)}
                                            >
                                                Delete
                                            </Button>
                                        )}
                                        {confirming && (
                                            <>
                                                <Button
                                                    size="sm"
                                                    variant="danger"
                                                    onClick={async () => {
                                                        setConfirmingDeleteId(null);
                                                        try { await destroy(user, row.id); }
                                                        catch (e) { setActionError(e.message); }
                                                    }}
                                                >
                                                    Confirm delete
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline-secondary"
                                                    onClick={() => setConfirmingDeleteId(null)}
                                                >
                                                    Cancel
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        </>
    );
}
